import React, { useEffect, useRef } from "react";
import TriloChatMessage from "./TriloChatMessage";
import TriloChatToolCalls from "./TriloChatToolCalls";
import TriloChatTypingIndicator from "./TriloChatTypingIndicator";
import { ChatIcon } from "./TriloChatIcons";

/**
 * TriloChatMessageList
 *
 * Scrolling conversation pane: the messages, the tool-call pills and the typing
 * indicator, with the pane kept scrolled to its newest content.
 *
 * Props:
 * - messages: Array of { role, content, options, drafts }
 * - sessionId: Active session id
 * - isStreaming: Whether a reply is still being worked on
 * - activeToolCalls: Tool names MW is currently running
 * - draftStates / setDraftStates: Draft state from hooks/useTriloChatDrafts.js
 */
const TriloChatMessageList = ({
  messages,
  sessionId,
  isStreaming,
  activeToolCalls,
  draftStates,
  setDraftStates,
}) => {
  const listRef = useRef(null);

  // Scroll this pane rather than calling scrollIntoView() on an anchor at the
  // bottom: scrollIntoView walks every scrollable ancestor, and the document
  // itself is taller than the viewport (the top bar sits above a 100vh grid).
  // `overflow-y: hidden` stops the user scrolling that overflow but not a
  // script, so the anchor version dragged the whole page up by the height of
  // the top bar and lifted the footer off the bottom of the screen.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  }, [messages, activeToolCalls]);

  return (
    <div className="trilochat__messages" ref={listRef}>
      {messages.length === 0 && !isStreaming && (
        <div className="trilochat__empty-state">
          <ChatIcon />
          <h3>Ready to help</h3>
          <p>
            Try asking &ldquo;list my studies&rdquo;, &ldquo;show me
            demographics for [study name]&rdquo;, &ldquo;explain why section X
            is failing&rdquo;, or &ldquo;what does the narrative say about
            [subject]&rsquo;s death?&rdquo;.
          </p>
        </div>
      )}

      {messages.map((message, i) => (
        <TriloChatMessage
          key={i}
          message={message}
          messageIndex={i}
          sessionId={sessionId}
          draftStates={draftStates}
          setDraftStates={setDraftStates}
        />
      ))}

      <TriloChatToolCalls toolCalls={activeToolCalls} />

      {isStreaming && activeToolCalls.length === 0 && (
        <TriloChatTypingIndicator />
      )}
    </div>
  );
};

export default TriloChatMessageList;
